import React, { useCallback, useEffect, useState } from "react";
import PerfectScrollbar from "react-perfect-scrollbar";
import clsx from "clsx";

// @material-ui/core components
import Typography from "@material-ui/core/Typography";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Badge from "@material-ui/core/Badge";
import Box from "@material-ui/core/Box";
import IconButton from "@material-ui/core/IconButton";
import LinearProgress from "@material-ui/core/LinearProgress";
import Link from "@material-ui/core/Link";
import makeStyles from "@material-ui/core/styles/makeStyles";

// @material-ui/icons components
import CloseIcon from "@material-ui/icons/Close";
import CheckIcon from "@material-ui/icons/Check";
import LocalShippingIcon from "@material-ui/icons/LocalShipping";
import LinkIcon from "@material-ui/icons/Link";
import CachedIcon from "@material-ui/icons/Cached";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import ReceiptIcon from "@material-ui/icons/Receipt";
import AddIcon from "@material-ui/icons/Add";
import PublishIcon from "@material-ui/icons/Publish";

// core components
import Api from "lib/api/httpRequestHelper";
import MaskAndValid from "utils/maskAndValid";
import componentStylesBadge from "assets/theme/components/badge.js";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";

const useStylesTimeline = makeStyles((theme) => ({
  timeline: {
    position: "relative",
    "&:before": {
      [theme.breakpoints.up("md")]: {
        left: "50%",
      },
      borderRightStyle: "dashed",
      borderRightColor: "#FEA9B0",
      content: "''",
      position: "absolute",
      top: 0,
      left: "3rem",
      height: "100%",
      borderRight: "2px solid " + theme.palette.stone.main,
    },
  },
  timelineOneSide: {
    paddingLeft: "0.7rem",
    borderRadius: "1rem",
    width: "fit-content",
    "&:before": {
      left: "1.5rem",
    },
    "& $timelineContent": {
      width: "auto",
      [theme.breakpoints.up("md")]: {
        maxWidth: "30rem",
      },
    },
    "& $timelineBadgeRoot": {
      [theme.breakpoints.up("md")]: {
        left: "-2px",
      },
    },
    "& $timelineBlock": {
      "&:nth-child(2n) $timelineContent": {
        float: "none",
      },
    },
  },
  timelineBlock: {
    position: "relative",
    margin: "2rem 0",
    "&:first-child": {
      marginTop: 0,
    },
    "&:last-child": {
      marginBottom: 0,
    },
    "&:after": {
      content: "''",
      display: "table",
      clear: "both",
    },
    "&:nth-child(2n) $timelineContent": {
      float: "right",
    },
  },
  timelineBadgeRoot: {
    position: "absolute",
    [theme.breakpoints.up("md")]: {
      left: "47.5%",
    },
  },
  timelineBadge: {
    width: "33px",
    height: "33px",
    borderRadius: "50%",
    display: "inline-flex",
    backgroundColor: "#FEA9B0",
  },
  pastTimelineSpots: {
    backgroundColor: "RGBA(255, 193, 197)",
  },
  timelineContent: {
    maxWidth: "max-content !important",
    marginLeft: "60px",
    paddingTop: ".5rem",
    position: "relative",
    top: " -6px",
    [theme.breakpoints.up("md")]: {
      width: "38%",
    },
  },
  typographyH5: {
    marginBottom: 0,
    marginTop: "1rem",
  },
  badgeMargin: {
    "&:not(:last-child)": {
      marginRight: ".5rem!important",
    },
  },
  commomBadgeColor: {
    backgroundColor: theme.palette.primary.main,
  },
  successBadgeColor: {
    backgroundColor: theme.palette.success.main,
  },
  paidBadgeColor: {
    backgroundColor: theme.palette.primary.badge,
  },
  rastreioLinkIcon: {
    textDecoration: "none",
    "&:hover": {
      color: theme.palette.primary.main,
    },
  },
}));

const useStylesBadge = makeStyles(componentStylesBadge);
const useStyles = makeStyles((theme) => ({
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
}));

export default function OrderDetailsTimelineDialog({ idPedido, handleClose }) {
  const occurrencesRef = React.useRef(null);
  const classes = {
    ...useStyles(),
    ...useStylesTimeline(),
    ...useStylesBadge(),
  };
  const snackbar = useSnackbar();
  const [timelineOrder, setTimelineOrder] = useState(null);

  const fetchTimelineOrder = useCallback(async () => {
    return await Api.get(
      "timeline-order",
      "order",
      "?idPedido=" + idPedido,
      true
    )
      .then((res) => {
        setTimelineOrder(res.Result);
      })
      .catch((error) => {
        snackbar.showMessage(error, {
          severity: "error",
        });      });
  }, [idPedido, snackbar]);

  useEffect(() => {
    fetchTimelineOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return idPedido ? (
    <Dialog open={Boolean(idPedido)} onClose={handleClose}>
      <DialogTitle disableTypography id="alert-dialog-title">
        <Typography variant="h2" component="h2">
          Linha do Tempo do Pedido {idPedido}
        </Typography>
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={handleClose}
        >
          <CloseIcon fontSize="large" />
        </IconButton>
      </DialogTitle>
      <PerfectScrollbar containerRef={() => occurrencesRef}>
        <DialogContent
          ref={occurrencesRef}
          style={{ padding: "0 1.5rem 1.5rem" }}
        >
          {timelineOrder?.length > 0 ? (
            <div className={clsx(classes.timeline, classes.timelineOneSide)}>
              {timelineOrder?.map((timeline, key) => {
                const handleVerifyDescription = () => {
                  if (timeline.Descricao.includes("\n")) {
                    const descriptionItens = timeline.Descricao.split("\n");

                    return descriptionItens?.map((item, index) => {
                      if (item.includes("Ocorrência de entrega")) {
                        return (
                          <Typography
                            key={`delivery-${index}`}
                            component="h5"
                            style={{ fontWeight: "bold" }}
                            variant="h5"
                            className={classes.typographyH5}
                          >
                            {item}
                          </Typography>
                        );
                      } else {
                        return (
                          <Typography
                            key={`description-${index}`}
                            component="h5"
                            variant="h5"
                            className={classes.typographyH5}
                          >
                            {item}
                          </Typography>
                        );
                      }
                    });
                  } else {
                    return (
                      <Typography
                        component="h5"
                        variant="h5"
                        className={classes.typographyH5}
                      >
                        {timeline.Descricao}
                      </Typography>
                    );
                  }
                };
                return (
                  <div key={key} className={classes.timelineBlock}>
                    <Badge
                      badgeContent={
                        timeline.Descricao.includes("Criação") ? (
                          <AddIcon />
                        ) : timeline.Descricao === "Pedido recebido" ? (
                          <ReceiptIcon />
                        ) : timeline.Descricao.includes("Upload") ? (
                          <PublishIcon />
                        ) : timeline.Descricao.includes("Link de Rastreio") ? (
                          <LocalShippingIcon />
                        ) : timeline.Descricao === "Pedido Aprovado" ||
                          timeline.Descricao === "Reserva no e-commerce" ? (
                          <CheckIcon />
                        ) : timeline.Descricao === "Pagamento Confirmado" ||
                          timeline.Descricao ===
                            "Confirmação de pagamento no e-commerce através do envio do XML" ? (
                          <AttachMoneyIcon />
                        ) : (
                          <CachedIcon />
                        )
                      }
                      classes={{
                        root: classes.timelineBadgeRoot,
                        badge: clsx(
                          classes.timelineBadge,
                          classes.badgePositionRelative,
                          { [classes.pastTimelineSpots]: key !== 0 },
                          { [classes.commomBadgeColor]: key !== 0 },
                          {
                            [classes.successBadgeColor]:
                              timeline.Descricao === "Pedido Aprovado",
                          },
                          {
                            [classes.paidBadgeColor]:
                              timeline.Descricao === "Pagamento Confirmado" ||
                              timeline.Descricao ===
                                "Confirmação de pagamento no e-commerce através do envio do XML",
                          }
                        ),
                      }}
                    ></Badge>
                    <div className={classes.timelineContent}>
                      <Box component="small" fontSize="80%" fontWeight="600">
                        {timeline.DataHora.includes("T00:00:00")
                          ? MaskAndValid.formattDateBR(timeline.DataHora)
                          : MaskAndValid.formattDateHourBR(timeline.DataHora)}
                      </Box>
                      {timeline.Descricao.includes("Link de Rastreio") ? (
                        <Box>
                          <Link
                            href={timeline.Descricao?.replace(
                              "Link de Rastreio ",
                              ""
                            )}
                            rel="noopener noreferrer"
                            target="_blank"
                            component="h5"
                            variant="h5"
                            className={classes.typographyH5}
                            style={{ cursor: "pointer" }}
                          >
                            <IconButton
                              component="a"
                              href={timeline.Descricao?.replace(
                                "Link de Rastreio ",
                                ""
                              )}
                              rel="noopener noreferrer"
                              target="_blank"
                              style={{ paddingLeft: 8 }}
                              color="primary"
                              classes={{
                                root: classes.rastreioLinkIcon,
                              }}
                            >
                              <span
                                style={{ marginRight: "8px", fontSize: "13px" }}
                              >
                                Link de Rastreio
                              </span>
                              <LinkIcon />
                            </IconButton>
                          </Link>
                        </Box>
                      ) : (
                        <>{handleVerifyDescription()}</>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={classes.root}>
              <span>Carregando Linha do Tempo...</span>
              <LinearProgress />
            </div>
          )}
        </DialogContent>
      </PerfectScrollbar>
    </Dialog>
  ) : null;
}
